import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useCanAccess, useNotify } from 'react-admin';
import { useSearchParams } from 'react-router-dom';

import {
    type ExpenseTransaction,
    type ReceiptCategory,
    type ResponsibleUser,
    fetchReceiptCategories,
    fetchRequestedTransactionId,
    fetchResponsibleUsers,
    fetchTransactions,
} from './receiptApi';
import { MyReceiptsList } from './MyReceiptsList';
import { MyExpensesList } from './MyExpensesList';
import { ReceiptUploadForm } from './ReceiptUploadForm';
import { BulkReceiptUpload } from './BulkReceiptUpload';

export const ReceiptsPage = () => {
    const [tab, setTab] = useState(0);
    const [categories, setCategories] = useState<ReceiptCategory[]>([]);
    const [users, setUsers] = useState<ResponsibleUser[]>([]);
    const [transactions, setTransactions] = useState<ExpenseTransaction[]>([]);
    const [searchParams] = useSearchParams();
    const requestedTransactionId =
        Number(searchParams.get('transaction')) || null;
    const [noticeTransactionId, setNoticeTransactionId] = useState<number | null>(
        null
    );
    const activeRequestId = requestedTransactionId || noticeTransactionId;
    const notify = useNotify();
    const { canAccess: canUpdateReceipts, isPending: canAccessPending } = useCanAccess({
        resource: 'receipts',
        action: 'edit',
    });

    useEffect(() => {
        void Promise.all([
            fetchReceiptCategories(),
            fetchResponsibleUsers(),
            fetchTransactions(),
        ])
            .then(([nextCategories, nextUsers, nextTransactions]) => {
                setCategories(nextCategories);
                setUsers(nextUsers);
                setTransactions(nextTransactions);
            })
            .catch(error => {
                // Offline-first: page still works for queued uploads / Pending UI.
                if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                    return;
                }
                notify(
                    error instanceof Error
                        ? error.message
                        : 'Could not load receipts data',
                    { type: 'warning' }
                );
            });
    }, [notify]);

    useEffect(() => {
        if (requestedTransactionId) {
            setTab(0);
            return;
        }
        void fetchRequestedTransactionId()
            .then(id => {
                if (!id) return;
                setNoticeTransactionId(id);
                setTab(0);
            })
            .catch(() => undefined);
    }, [requestedTransactionId]);

    useEffect(() => {
        // Reset tab if user loses access to Expenses tab while viewing it
        if (!canAccessPending && !canUpdateReceipts && tab === 2) {
            setTab(0);
        }
    }, [canUpdateReceipts, canAccessPending, tab]);

    const requestedTransaction =
        transactions.find(
            item => item.id === activeRequestId && !item.receipt_id
        ) || null;
    const helped = users.filter(user => user.helped);
    const helpers = users.filter(user => user.helps_me).map(user => user.name || user.email);
    // null = my own receipts and charges; otherwise the coworker I'm helping.
    const [ownerId, setOwnerId] = useState<number | null>(null);
    const [bulkMode, setBulkMode] = useState(false);

    return (
        <Box py={3}>
            <Typography variant="h4" gutterBottom>
                Receipts
            </Typography>
            {helpers.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    {helpers.join(' and ')} can help you with receipts: upload them, match them to your card
                    charges and post them to the admin. Anything they do shows "Uploaded by" or "Posted by" with
                    their name.
                </Alert>
            )}
            <Tabs
                value={tab}
                onChange={(_, value) => setTab(value)}
                sx={{ mb: 3 }}
            >
                <Tab label="Receipt upload" />
                <Tab label="My receipts" />
                {canUpdateReceipts && <Tab label="Expenses" />}
            </Tabs>
            {tab > 0 && helped.length > 0 && (
                <TextField
                    select
                    size="small"
                    label="Viewing"
                    value={ownerId ?? ''}
                    onChange={event =>
                        setOwnerId(event.target.value === '' ? null : Number(event.target.value))
                    }
                    sx={{ mb: 2, minWidth: 240 }}
                >
                    <MenuItem value="">Me</MenuItem>
                    {helped.map(user => (
                        <MenuItem key={user.id} value={user.id}>
                            {user.name || user.email}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            {tab === 0 && (
                <Box>
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={bulkMode ? 'bulk' : 'single'}
                        onChange={(_, value) => {
                            if (value) setBulkMode(value === 'bulk');
                        }}
                        sx={{ mb: 2 }}
                    >
                        <ToggleButton value="single">One upload</ToggleButton>
                        <ToggleButton value="bulk">Bulk upload</ToggleButton>
                    </ToggleButtonGroup>
                    {bulkMode ? (
                        <BulkReceiptUpload
                            categories={categories}
                            users={users}
                            onUploaded={() => {
                                void fetchTransactions().then(setTransactions);
                            }}
                        />
                    ) : (
                        <ReceiptUploadForm
                            categories={categories}
                            users={users}
                            requestedTransaction={requestedTransaction}
                            busy={false}
                            onUploaded={() => {
                                void fetchTransactions().then(setTransactions);
                            }}
                            onViewMyReceipts={() => setTab(1)}
                        />
                    )}
                </Box>
            )}
            {tab === 1 && (
                <MyReceiptsList categories={categories} users={users} ownerId={ownerId} />
            )}
            {tab === 2 && canUpdateReceipts && (
                <MyExpensesList users={users} categories={categories} ownerId={ownerId} />
            )}
        </Box>
    );
};
